import { useMemo } from 'react';
import { useStaffProfiles, type StaffProfile } from '../api/staffProfiles';

/**
 * Resolve a staff member's role tokens from the staff-profile roster (supervision
 * revamp Task 15). Surfaces reference staff by different identifiers — a Dataverse
 * `systemuserid` (action-item `bookkeeperId`, meeting `bookkeeperId`), a display
 * name (`ClientDetailPage` owner formatted value), or the core `StaffId` — so the
 * match is deliberately liberal: identifier is compared, case-insensitively, against
 * a profile's `staffId`, `systemUserId`, and `fullName`.
 *
 * Pure so it can be unit-tested without React/query plumbing.
 */
export function resolveStaffRoles(
  profiles: StaffProfile[] | undefined,
  identifier: string | null | undefined,
): string[] {
  if (!profiles || !identifier) return [];
  const needle = identifier.trim().toLowerCase();
  if (!needle) return [];

  const match = profiles.find((p) => {
    const candidates = [p.staffId, p.systemUserId, p.fullName];
    return candidates.some(
      (c) => typeof c === 'string' && c.trim().toLowerCase() === needle,
    );
  });

  return match?.roles ?? [];
}

/**
 * Hook wrapper over {@link resolveStaffRoles}. Reads the staff-profile roster —
 * open to any authenticated staff, so badges render for everyone — and returns the
 * matched member's roles, or `[]` when the roster is unavailable or no identifier
 * matches. Badges then render nothing, degrading gracefully.
 */
export function useStaffRoles(identifier: string | null | undefined): string[] {
  const { data: profiles } = useStaffProfiles();
  return useMemo(() => resolveStaffRoles(profiles, identifier), [profiles, identifier]);
}
